import { type UseMutationResult } from "@tanstack/react-query";
import * as React from "react";

// Lazy: highlight.js is heavy and most threads have no text artifact to preview.
const HighlightedCodeBody = React.lazy(
  () => import("@/shared/components/HighlightedCode/HighlightedCodeBody"),
);

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  useArtifactText,
  useArtifactUrl,
  useDownloadArtifact,
} from "@/shared/domain/agentThread/hooks";
import { Download, FileText, TriangleAlert } from "@/shared/foundation/icons";
import { formatByteSize } from "@/shared/text";

import styles from "./ArtifactStrip.module.css";
import { CodeBlock } from "./CodeBlock";
import { INLINE_IMAGE_CONTENT_TYPES } from "./inlineImageContentTypes";
import { type ArtifactOutput, notReturnedReason } from "./outputsField";

/**
 * Content types previewed inline as text via `CodeBlock`, never a markdown/HTML renderer. An
 * allowlist without `text/html`: parsing produced markup is one step from executing it.
 */
const INLINE_TEXT_CONTENT_TYPES: ReadonlySet<string> = new Set([
  "text/plain",
  "text/csv",
  "text/markdown",
  "text/x-python",
  "text/tab-separated-values",
  "application/json",
]);

/**
 * `CodeBlock` label and highlighting grammar per content type. TSV must never map to `csv`, whose
 * renderer splits on commas; a label with no grammar just renders plain `<code>`.
 */
const TEXT_PREVIEW_LANGUAGES: Partial<Record<string, string>> = {
  "text/csv": "csv",
  "application/json": "json",
  "text/x-python": "python",
  "text/markdown": "markdown",
  "text/tab-separated-values": "tsv",
};

/**
 * Largest artifact previewed inline; larger ones get a download row. Previews are fetched and
 * rendered whole on mount (a `<span>` per CSV cell), and the sandbox allows files up to 64 MiB.
 */
const MAX_INLINE_TEXT_PREVIEW_BYTES = 256 * 1024;

/** A 404 means the artifact expired (30-day lifecycle): expected for old threads, not a crash. */
function isNoLongerAvailable(error: Error | null): boolean {
  return error?.name === "RobotoNotFoundException";
}

function ArtifactErrorNote({
  name,
  error,
  action,
  component = "p",
}: {
  name: string;
  error: Error | null;
  action: "load" | "download";
  component?: "p" | "span";
}): React.JSX.Element {
  let message = `Failed to ${action} ${name}.`;
  if (isNoLongerAvailable(error)) {
    message =
      "This file is no longer available. Files a tool produces are kept for 30 days.";
  }
  return (
    <RobotoTypography component={component} className={styles.reason}>
      {message}
    </RobotoTypography>
  );
}

interface ArtifactRowProps {
  threadId: string;
  toolUseId: string;
  artifact: ArtifactOutput;
}

/** A produced file that was never uploaded (too large, budget exhausted, ...): name, size, and why. */
function NotReturnedRow({
  artifact,
}: {
  artifact: ArtifactOutput;
}): React.JSX.Element {
  return (
    <div className={styles.row}>
      <TriangleAlert aria-hidden className={styles.mutedIcon} />
      <span className={styles.fileName}>{artifact.name}</span>
      <span className={styles.fileSize}>{formatByteSize(artifact.bytes)}</span>
      <RobotoTypography component="span" className={styles.reason}>
        {notReturnedReason(artifact.status)}
      </RobotoTypography>
    </div>
  );
}

/** Download button; each click mints a fresh presigned URL so it never follows an expired link. */
function DownloadButton({
  artifactName,
  download,
}: {
  artifactName: string;
  download: UseMutationResult<void, Error, void>;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={styles.downloadButton}
      onClick={() => download.mutate()}
      disabled={download.isPending}
      aria-label={`Download ${artifactName}`}
    >
      <Download aria-hidden className={styles.downloadIcon} />
      {download.isPending ? "Downloading…" : "Download"}
    </button>
  );
}

/** name + size + Download, for any uploaded artifact this strip doesn't render inline. */
function DownloadOnlyRow({
  threadId,
  toolUseId,
  artifact,
  note,
}: ArtifactRowProps & { note?: string }): React.JSX.Element {
  const download = useDownloadArtifact(threadId, toolUseId, artifact.name);

  return (
    <div className={styles.row}>
      <FileText aria-hidden className={styles.fileIcon} />
      <span className={styles.fileName}>{artifact.name}</span>
      <span className={styles.fileSize}>{formatByteSize(artifact.bytes)}</span>
      {note !== undefined && (
        <RobotoTypography component="span" className={styles.reason}>
          {note}
        </RobotoTypography>
      )}
      <DownloadButton artifactName={artifact.name} download={download} />
      {download.isError && (
        <ArtifactErrorNote
          name={artifact.name}
          error={download.error}
          action="download"
          component="span"
        />
      )}
    </div>
  );
}

/** Shell for inline preview rows: name, size and Download above the caller's preview body. */
function ArtifactPreviewTile({
  threadId,
  toolUseId,
  artifact,
  children,
}: ArtifactRowProps & { children?: React.ReactNode }): React.JSX.Element {
  const download = useDownloadArtifact(threadId, toolUseId, artifact.name);

  return (
    <div className={styles.tile}>
      <div className={styles.tileHeader}>
        <span className={styles.fileName}>{artifact.name}</span>
        <span className={styles.fileSize}>
          {formatByteSize(artifact.bytes)}
        </span>
        <DownloadButton artifactName={artifact.name} download={download} />
      </div>
      {download.isError && (
        <ArtifactErrorNote
          name={artifact.name}
          error={download.error}
          action="download"
        />
      )}
      {children}
    </div>
  );
}

function PreviewLoading(): React.JSX.Element {
  return (
    <RobotoTypography component="p" className={styles.loading}>
      Loading preview…
    </RobotoTypography>
  );
}

/**
 * Inline `<img>` from a presigned URL. A cached URL may have expired, so a load error mints a new
 * URL once; a second failure, or a re-mint that returns the same URL, is reported.
 */
function ImageRow({
  threadId,
  toolUseId,
  artifact,
}: ArtifactRowProps): React.JSX.Element {
  const { data, isError, error, refetch } = useArtifactUrl(
    threadId,
    toolUseId,
    artifact.name,
  );
  const [failedUrl, setFailedUrl] = React.useState<string>();
  const [gaveUp, setGaveUp] = React.useState(false);
  const url = data?.url;

  const handleImageError = () => {
    if (failedUrl === undefined && url !== undefined) {
      setFailedUrl(url);
      void refetch().then((result) => {
        // S3 can sign the same URL again within a second; nothing new to try.
        if (result.data?.url === url) {
          setGaveUp(true);
        }
      });
    } else {
      setGaveUp(true);
    }
  };

  // A failed refetch keeps the stale `data`, so check the error first.
  let body: React.ReactNode = <PreviewLoading />;
  if (isError) {
    body = (
      <ArtifactErrorNote name={artifact.name} error={error} action="load" />
    );
  } else if (gaveUp) {
    body = (
      <ArtifactErrorNote name={artifact.name} error={null} action="load" />
    );
  } else if (url !== undefined && url !== failedUrl) {
    body = (
      <img
        src={url}
        alt={artifact.name}
        className={styles.image}
        onError={handleImageError}
      />
    );
  }

  return (
    <ArtifactPreviewTile
      threadId={threadId}
      toolUseId={toolUseId}
      artifact={artifact}
    >
      {body}
    </ArtifactPreviewTile>
  );
}

/**
 * Inline text preview fetched from S3 via a presigned URL. The fetch is cross-origin, so it needs
 * the artifact bucket's CORS rule to allow this app's origin (images and downloads don't).
 */
function TextRow({
  threadId,
  toolUseId,
  artifact,
}: ArtifactRowProps): React.JSX.Element {
  const {
    data: text,
    isPending,
    isError,
    error,
  } = useArtifactText(threadId, toolUseId, artifact.name);
  const language = TEXT_PREVIEW_LANGUAGES[artifact.content_type];

  return (
    <ArtifactPreviewTile
      threadId={threadId}
      toolUseId={toolUseId}
      artifact={artifact}
    >
      {isPending && <PreviewLoading />}
      {isError && (
        <ArtifactErrorNote name={artifact.name} error={error} action="load" />
      )}
      {text !== undefined && (
        // Highlighted only, never rendered as markdown/HTML: a produced file is data.
        <CodeBlock
          code={text}
          language={language}
          className={styles.textPreview}
          highlightedChildren={
            language && (
              <React.Suspense fallback={<code>{text}</code>}>
                <HighlightedCodeBody code={text} language={language} />
              </React.Suspense>
            )
          }
        />
      )}
    </ArtifactPreviewTile>
  );
}

export interface ArtifactStripProps {
  threadId: string;
  toolUseId: string;
  artifacts: ArtifactOutput[];
}

/**
 * One row per file a tool call produced. Only `uploaded` files are downloadable, and only the
 * allowlisted content types preview inline.
 */
export function ArtifactStrip({
  threadId,
  toolUseId,
  artifacts,
}: ArtifactStripProps): React.JSX.Element {
  return (
    <div className={styles.strip}>
      {artifacts.map((artifact, index) => {
        // Index in the key: sanitized, truncated filenames can collide. The list never changes.
        const key = `${index}-${artifact.name}`;

        if (artifact.status !== "uploaded") {
          return <NotReturnedRow key={key} artifact={artifact} />;
        }

        if (INLINE_IMAGE_CONTENT_TYPES.has(artifact.content_type)) {
          return (
            <ImageRow
              key={key}
              threadId={threadId}
              toolUseId={toolUseId}
              artifact={artifact}
            />
          );
        }

        if (INLINE_TEXT_CONTENT_TYPES.has(artifact.content_type)) {
          // Uses the recorded size, so an oversized artifact triggers no fetch at all.
          if (artifact.bytes <= MAX_INLINE_TEXT_PREVIEW_BYTES) {
            return (
              <TextRow
                key={key}
                threadId={threadId}
                toolUseId={toolUseId}
                artifact={artifact}
              />
            );
          }

          return (
            <DownloadOnlyRow
              key={key}
              threadId={threadId}
              toolUseId={toolUseId}
              artifact={artifact}
              note="Too large to preview"
            />
          );
        }

        return (
          <DownloadOnlyRow
            key={key}
            threadId={threadId}
            toolUseId={toolUseId}
            artifact={artifact}
          />
        );
      })}
    </div>
  );
}
